import { Component } from '@theme/component';

const SHOWN_KEY = 'vg-newsletter-popup:shown:v1';
const PENDING_KEY = 'vg-newsletter-popup:pending:v1';
const ATTEMPT_KEY = 'vg-newsletter-popup:discount-attempt:v1';
const claimedAttempts = new Set();
const discountRequests = new Map();
let shownInMemory = false;

/** Read optional session state without making storage a runtime requirement. */
function readSession(key) {
  try { return sessionStorage.getItem(key); } catch { return null; }
}

/** Persist no email, customer identifier, or marketing preference. */
function writeSession(key, value) {
  try { sessionStorage.setItem(key, value); } catch { /* In-memory suppression still applies. */ }
}

function normalizeCode(code) {
  return typeof code === 'string' ? code.trim().toUpperCase() : '';
}

function readCodes(cart) {
  if (cart === null) return [];
  if (!cart || !Array.isArray(cart.discountCodes) || !cart.discountCodes.every((entry) => typeof entry?.code === 'string' && typeof entry.applicable === 'boolean')) throw new Error('Unknown discount state');
  return cart.discountCodes;
}

function codeState(cart, code) {
  const codes = readCodes(cart);
  if (codes.some((entry) => normalizeCode(entry.code) !== normalizeCode(code))) return 'conflict';
  const current = codes.find((entry) => normalizeCode(entry.code) === normalizeCode(code));
  if (!current) return 'unknown';
  return current.applicable && cart.totalQuantity > 0 ? 'applied' : 'remembered';
}

async function readCurrentCart() {
  if (typeof window.Shopify?.actions?.getCart !== 'function') throw new Error('Cart inspection unavailable');
  const result = await window.Shopify.actions.getCart({}, { signal: AbortSignal.timeout(10000) });
  readCodes(result?.cart);
  return result.cart;
}

async function prepareDiscount(code) {
  if (!window.Shopify?.actions?.getCart && document.readyState !== 'complete') {
    await new Promise((resolve) => {
      const timer = setTimeout(resolve, 5000);
      document.addEventListener('DOMContentLoaded', () => { clearTimeout(timer); resolve(); }, { once: true });
    });
  }
  const before = await readCurrentCart();
  const beforeState = codeState(before, code);
  if (beforeState !== 'unknown') return beforeState;
  const root = window.Shopify?.routes?.root;
  if (typeof root !== 'string' || !root.startsWith('/') || root.startsWith('//')) throw new Error('Invalid cart route');
  const cartUrl = new URL(`${root}cart.js`, location.origin);
  const updateUrl = new URL(`${root}cart/update.js`, location.origin);
  if (cartUrl.origin !== location.origin || updateUrl.origin !== location.origin) throw new Error('Invalid cart origin');
  const response = await fetch(cartUrl, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(10000) });
  if (!response.ok) throw new Error('Cart inspection failed');
  const ajaxCart = await response.json();
  if (!Array.isArray(ajaxCart.items) || !Array.isArray(ajaxCart.cart_level_discount_applications) || typeof ajaxCart.total_discount !== 'number') throw new Error('Unknown cart state');
  if (ajaxCart.total_discount > 0 || ajaxCart.cart_level_discount_applications.length || ajaxCart.items.some((item) => item.discounts?.length || item.line_level_discount_allocations?.length)) return 'conflict';
  try {
    const updated = await fetch(updateUrl, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }), signal: AbortSignal.timeout(10000),
    });
    if (!updated.ok) throw new Error('Discount request failed');
  } catch {
    return codeState(await readCurrentCart(), code);
  }
  return codeState(await readCurrentCart(), code);
}

/** @extends {Component<{dialog: HTMLDialogElement, close: HTMLButtonElement, decline: HTMLButtonElement, consent?: HTMLInputElement, result?: HTMLElement}>} */
class NewsletterPopup extends Component {
  #controller;
  #timer;
  #previousFocus;
  #dialog;
  #responsePending = false;
  #applying = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog, close, decline, consent, result } = this.refs;
    if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') return;
    this.#dialog = dialog;

    close?.addEventListener('click', () => dialog.close(), { signal });
    decline?.addEventListener('click', () => dialog.close(), { signal });
    dialog.addEventListener('close', () => this.#restoreFocus(), { signal });
    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const controls = [...dialog.querySelectorAll('button, a[href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')].filter((element) => !element.matches(':disabled') && element.getClientRects().length);
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }, { signal });
    const form = dialog.querySelector('form');
    form?.addEventListener('invalid', () => writeSession(PENDING_KEY, ''), { signal, capture: true });
    form?.addEventListener('submit', (event) => {
      writeSession(PENDING_KEY, '');
      if (!(consent instanceof HTMLInputElement) || !consent.checked || !form.checkValidity()) {
        event.preventDefault();
        form.reportValidity();
        return;
      }
      queueMicrotask(() => {
        if (!event.defaultPrevented) writeSession(PENDING_KEY, JSON.stringify({ formId: this.dataset.formId, path: location.pathname, time: Date.now() }));
      });
    }, { signal });

    let pending;
    try { pending = JSON.parse(readSession(PENDING_KEY) || 'null'); } catch { pending = null; }
    const ownsPending = pending !== null && typeof pending === 'object' && !Array.isArray(pending) && pending.formId === this.dataset.formId && pending.path === location.pathname && typeof pending.time === 'number' && Number.isFinite(pending.time) && Date.now() - pending.time >= 0 && Date.now() - pending.time < 600000;
    const ownsReturn = location.hash === `#${this.dataset.formId}` || ownsPending;
    if (result && ownsReturn) {
      if (result.dataset.result === 'success' && this.dataset.editor !== 'true') {
        this.refs.applyDiscount?.addEventListener('click', () => this.#applyDiscount(), { signal });
        if (this.dataset.hasOrders === 'true') this.#discountStatus('ineligible');
        else if (ownsPending) {
          const claim = JSON.stringify([pending.formId, pending.path, pending.time, result.dataset.discountCode]);
          const claimed = claimedAttempts.has(claim) || readSession(ATTEMPT_KEY) === claim;
          claimedAttempts.add(claim);
          writeSession(ATTEMPT_KEY, claim);
          writeSession(PENDING_KEY, '');
          if (!claimed) void this.#applyDiscount();
        }
      }
      this.#responsePending = true;
      document.addEventListener('visibilitychange', () => this.#showResponse(), { signal });
      this.#showResponse();
      return;
    }
    if (pending) writeSession(PENDING_KEY, '');
    // Never show a response from a different customer form as this popup's confirmation.
    if (result || this.dataset.autoEligible === 'false' || this.dataset.editor === 'true' || shownInMemory || readSession(SHOWN_KEY)) return;

    const seconds = Number(this.dataset.delay);
    this.#timer = setTimeout(() => this.#show(false), (Number.isFinite(seconds) ? Math.max(5, seconds) : 12) * 1000);
  }

  async #applyDiscount() {
    const result = this.refs.result;
    const code = result?.dataset.discountCode?.trim();
    if (this.#applying || !code || result.dataset.result !== 'success' || this.dataset.editor === 'true' || this.dataset.hasOrders === 'true') return;
    this.#applying = true;
    this.#discountStatus('applying');
    const requestKey = JSON.stringify([this.dataset.formId, location.pathname, code]);
    if (!discountRequests.has(requestKey)) discountRequests.set(requestKey, prepareDiscount(code).catch(() => 'unknown'));
    const state = await discountRequests.get(requestKey);
    this.#applying = false;
    if (this.isConnected) this.#discountStatus(state);
  }

  #discountStatus(state) {
    const { result, discountStatus, applyDiscount, discountFallback, discountLink } = this.refs;
    const message = result?.querySelector(`[data-discount-message="${state}"]`);
    if (discountStatus && message) discountStatus.textContent = message.textContent;
    if (result) result.dataset.discountState = state;
    if (applyDiscount) {
      applyDiscount.disabled = true;
      applyDiscount.hidden = state !== 'applying';
    }
    if (discountFallback) discountFallback.hidden = state !== 'unknown';
    if (state === 'unknown' && discountLink && result?.dataset.discountCode) {
      const target = new URL(location.href);
      target.searchParams.delete('customer_posted');
      target.hash = '';
      const link = new URL(`/discount/${encodeURIComponent(result.dataset.discountCode)}`, location.origin);
      link.searchParams.set('redirect', `${target.pathname}${target.search}`);
      discountLink.href = link.href;
    }
  }

  #showResponse() {
    if (!this.#responsePending) return;
    clearTimeout(this.#timer);
    if (this.#show(true)) {
      this.#responsePending = false;
      writeSession(PENDING_KEY, '');
    } else {
      this.#timer = setTimeout(() => this.#showResponse(), 1000);
    }
  }

  #show(isResponse) {
    const dialog = this.#dialog;
    if (!this.isConnected || !dialog || dialog.open || document.hidden) return false;
    const visibleModal = [...document.querySelectorAll('[aria-modal="true"]')].some((element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    if (document.querySelector('dialog[open]') || visibleModal) return false;
    const active = document.activeElement;
    if (!isResponse && active instanceof HTMLElement && (active.matches('input, textarea, select') || active.isContentEditable)) return false;
    this.#previousFocus = active instanceof HTMLElement ? active : null;
    dialog.showModal();
    shownInMemory = true;
    writeSession(SHOWN_KEY, '1');
    if (isResponse) {
      this.refs.result?.focus({ preventScroll: true });
      requestAnimationFrame(() => {
        if (dialog.open) {
          if (document.activeElement === document.body) this.refs.result?.focus({ preventScroll: true });
          dialog.scrollTop = 0;
        }
      });
    }
    return true;
  }

  #restoreFocus() {
    if (this.#previousFocus?.isConnected && this.#previousFocus !== document.body) this.#previousFocus.focus({ preventScroll: true });
    this.#previousFocus = null;
  }

  #cleanup() {
    clearTimeout(this.#timer);
    this.#controller?.abort();
    this.#responsePending = false;
    if (this.#dialog?.open) this.#dialog.close();
    this.#restoreFocus();
    this.#dialog = null;
  }
}

if (!customElements.get('vg-newsletter-popup')) customElements.define('vg-newsletter-popup', NewsletterPopup);
